import { ReactComponent as Error } from '@/assets/svgs/icon-error-message-exclamation.svg';
import Button from '@/components/Button';
import DexLeftComponent from '@/components/DexLeftComponent';
import IconEye from '@/components/IconEye';
import Input from '@/components/Input';
import googleKeys from '@/constants/configuration/google-keys.json';
import useAuthLogin from '@/hooks/use-auth-login';
import usePage from '@/hooks/use-page';
import useStepList from '@/hooks/use-step-list';
import { useLocation, useNavigate } from '@/lib/router-dom';
import { login } from '@/redux/actions/auth.js';
import API_LOGIN from '@/services/api/login';
import { getDexConfig } from '@/utils/get-dex-config';
import { getEnvAndDex } from "@/utils/get-env-and-dex";
import { validateDomain } from '@/utils/validate-domain.js';
import { validateEmail } from '@/utils/validate-email.js';
import classname from 'classnames';
import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useTradex } from "@/hooks/use-tradex/index";

const Page = ({ defaultPasswordField = false }) => {
	/*=================================================
		General
	/================================================*/

	usePage({ title: 'Login' })

	const [errorMessage, setErrorMessage] = useState()
	const [loading, setLoading] = useState(false);
	const [customMask, setCustomMask] = useState(true);
	const { env, dex } = getEnvAndDex();

	/*=================================================
		API
	/================================================*/

	const dispatch = useDispatch()
	const tfa = useSelector(state => state.auth.tfa)

	const { goTo, replace } = useNavigate();
	const { currentPath, currentState } = useLocation();

	const { register, handleSubmit, formState: { errors, isValid }, setValue, getValues } = useForm({
		mode: 'onChange'
	})

	const { authLoginData, getAuthorizationURL } = useAuthLogin();

	/*=================================================
		API Action Functions
	/================================================*/

	const [passwordField, setPasswordField] = useState(defaultPasswordField)

	const redirectURL = encodeURI(window.location.origin + `/auth/redirect`);
	const [showLoginPage, setShowLoginPage] = useState(false);
	const [changeButtonTextForLockedUsers, setChangeButtonTextForLockedUsers] = useState(false)
	const captchaRef = useRef(null)
	const defaultCaptchaValue = googleKeys[dex]?.[env] && googleKeys[dex]?.[env].CAPTCHA_SITE_KEY;
	const [captchaToken, setCaptchaToken] = useState(!defaultCaptchaValue)

	useEffect(() => {
		const body = {
			redirectUrl: redirectURL
		}

		getAuthorizationURL(body).then((data) => {
			if (localStorage.getItem("userEmail")) {
				window.open(data.sso.authUrl, "_self")
			}
			else {
				setShowLoginPage(true)
			}
		})
			.catch((e) => {
				setErrorMessage(e.message ? e.message : "Something went wrong! Please try again.")
			})

		if (currentState) {
			let message;

			if (currentState?.state?.message.includes("invalid_client")) {
				message = "There was a problem with the SSO login. Please contact the administrator."
			}
			else if (currentState?.state?.message === "credentialError") {
				message = "We are unable to log you in. Please contact the administrator."
			}
			else {
				message = currentState?.state?.message
			}

			setErrorMessage(message)
			replace(currentPath, {});
		}
	}, [])

	useEffect(() => {
		if (tfa) {
			nextStep()
		}
	}, [tfa])

	async function handleChangeReCaptcha(value) {
		setLoading(true)
		const [error] = await API_LOGIN.siteVerify({ token: value })
		setLoading(false)
		if (error) {
			setCaptchaToken(false)
			if (captchaRef.current) {
				captchaRef.current.reset();
			}
			return setErrorMessage("reCAPTCHA verification failed")
		}
		setCaptchaToken(true)
	}

	function resetValue() {
		setValue('password', "");
		setValue('secret', "");
	}

	async function loginHandler(data) {

		if (changeButtonTextForLockedUsers) {
			return window.location.reload()
		}

		if (loading) {
			return
		}

		setErrorMessage(null)
		setLoading(true)

		try {
			if (!passwordField) {
				localStorage.setItem("userEmail", data.email)
				window.open(authLoginData.authUrl, "_self")
			}
			else {
				await dispatch(login(goTo, data, "login"))
			}
		} catch (e) {
			if (e.message === "credentialError") {
				setErrorMessage("Incorrect password or email address.")
			}
			else if (e.message.includes("Your account has been locked")) {
				setErrorMessage("Your account has been locked. We sent a link to reset your password to the provided email account.")
				return setChangeButtonTextForLockedUsers(true)
			}
			else {
				setErrorMessage(e.message)
			}
			resetValue()
		}
		setLoading(false)
	}

	async function otpHandler(data) {
		if (loading) {
			return
		}

		setErrorMessage(null)
		setLoading(true)

		try {
			const dataWithSecret = {
				otp: data.otp
			}
			await dispatch(login(goTo, dataWithSecret, "otpVerify"))
		} catch (e) {
			setErrorMessage(e.message)
			resetValue()
		}

		setLoading(false)
	}

	function handleChange(input) {
		if (!validateEmail(input)) {
			setPasswordField(false);
			return false;
		}
		const validDomain = validateDomain(input, authLoginData?.domains);

		if (!validDomain) {
			setPasswordField(true);
		} else {
			setPasswordField(false);
		}
		return true
	}

	window.recaptchaOptions = {
		useRecaptchaNet: true,
	};

	/*=================================================
		Step
	/================================================*/

	const { step, nextStep } = useStepList()

	const passwordInputClass = classname({
		'input--error': errors.password,
		'login-box__password': true
	})

	/*=================================================
		MultiDex
	/================================================*/
	const isTradex = useTradex();
	const { dexName } = getDexConfig(dex);

	return (
		showLoginPage && (
			<div className='layout-login'>
				<DexLeftComponent />
				<div className='layout-login__right'>
					<div className='login-box'>
						{
							step === 1 && (
								<>
									<div className='login-box__title'>
										Welcome to {isTradex ? "SGTraDex" : dexName} Admin Portal
									</div>
									<div className='login-box__sub-title'>Please log in to continue</div>
									<div className='login-box__content'>
										<form className='login-box__form' onSubmit={handleSubmit(data => loginHandler(data))} autoComplete="off" data-testid="form">
											<div className='login-box__form__cols'>
												{errorMessage && (
													<div className='login-box__form__col--error'>
														<Error /> <span className='t--required'>{errorMessage}</span>
													</div>
												)}
												<div className='login-box__form__col'>
													<Input
														name="email"
														register={register({
															required: "This field is mandatory",
															validate: {
																checkEmail: v => handleChange(v) || "Please enter a valid email"
															}
														})}
														placeholder="Email"
														error={errors.email}
														errorMessage={errors.email?.message}
														onBlur={(e) => { e.target.value = e.target.value.trim() }}
														// onChange={(e)=> {handleChange(e.target.value)}}
														autoComplete="off"
														disabled={changeButtonTextForLockedUsers}
													/>
												</div>
												{
													passwordField && (
														<>
															<div className='login-box__form__col'>
																<Input
																	name='password'
																	className={passwordInputClass}
																	type={customMask ? 'password' : 'text'}
																	placeholder="Password"
																	register={register({
																		required: passwordField
																	})}
																	autoComplete="off"
																	data-testid="secret-input"
																	disabled={changeButtonTextForLockedUsers}
																/>
																<span data-testid="reveal-btn" className="link icon-eye" onClick={() => setCustomMask(!customMask)}>
																	<IconEye bool={customMask} />
																</span>
															</div>
															<div className='login-box__form__col login-box__form__col--row'>
																<div className="login-box__form__col__left loging-box__text  loging-box__text--theme link">
																	<span id="forgot-password-trigger-id" onClick={() => goTo(`/forget-password`, { email: getValues("email") })}>Forgot Password?</span>
																</div>
															</div>
														</>
													)
												}
												{googleKeys[dex]?.[env]?.CAPTCHA_SITE_KEY && (
													<ReCAPTCHA sitekey={`${googleKeys[dex]?.[env]?.CAPTCHA_SITE_KEY}`} ref={captchaRef} style={{ marginTop: "20px" }} onChange={(value) => handleChangeReCaptcha(value)} data-testid="recaptcha-test" />
												)
												}

												<div className='login-box__form__actions'>
													{!changeButtonTextForLockedUsers && <Button type='submit' text="Log In" id="login-button-trigger-id" maxWidth="168" full round loading={loading}
														disabled={!isValid /*|| (defaultCaptchaValue && !captchaToken)*/}
													/>}
													{changeButtonTextForLockedUsers && <Button text="Back to Log In" type="submit" maxWidth="168" full round />}
												</div>
											</div>
										</form>
									</div>
									<div className='login-box__desc'>
										New to {isTradex ? "SGTraDex" : dexName}? <span className="link" id="sign-up-here-trigger-id" onClick={() => goTo(`/sign-up`)}>Sign up here.</span>
									</div>
								</>
							)
						}

						{
							step === 2 && (
								<>
									<div className='login-box__title'>Welcome to {isTradex ? "SGTraDex" : dexName} Admin Portal</div>
									<div className='login-box__sub-title'>Enter the Security Code generated in Google Authenticator App.</div>
									<div className='login-box__content'>
										<form className='login-box__form' onSubmit={handleSubmit(data => otpHandler(data))} autoComplete="off" data-testid="otp-form">
											<div className='login-box__form__cols'>
												{errorMessage && (
													<div className='login-box__form__col--error'>
														<Error /> <span className='t--required'>{errorMessage}</span>
													</div>
												)}
												<div className='login-box__form__col'>
													<Input
														maxWidth="360"
														name="otp"
														placeholder="000000"
														register={register({
															required: "This field is mandatory",
															minLength: {
																value: 6,
																message: "Please enter a valid OTP"
															}
														})}
														error={errors.otp}
														errorMessage={errors.otp?.message}
														maxLength={6}
													/>
												</div>
												<div className='login-box__form__col login-box__form__col--row'>
													<div className="login-box__form__col__left loging-box__text  loging-box__text--theme link">
														<span onClick={() => goTo(`/reset-2fa`)}>Trouble with Security Code?</span>
													</div>
												</div>
												<div className='login-box__form__actions'>
													<Button text="Verify & Login" id="verify-activate-submit-id" type="submit" maxWidth="168" full round disabled={!isValid} loading={loading} />
												</div>
											</div>
										</form>
									</div>
								</>
							)
						}
					</div>
				</div>

				<div className="version__no">
					v1.6.12
				</div>
			</div>
		)
	)
}

export default Page
